import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaClone } from '@react-icons/all-files/fa/FaClone';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset, archivarInstancia, revertirArchivadoInstancia } from '../../../../manager/profase-periodo-actividad-instancia.reducer';
import {
  buildInstanciaTipoResumenParams,
  PERIODO_INSTANCIA_TABLE_FILTERS,
  setTipoPeriodoFilter,
} from '../../../../shared/periodo-actividad-instancia-filters';
import PeriodoTipoResumenBar, { type PeriodoTipoResumen } from '../../../../shared/periodo-tipo-resumen-bar';
import { formatTipoPeriodoLabel } from '../../../../shared/workflow-periodicidad';

const BASE = '/configuracion/calendario/periodos/periodo-instancia';
const PERIODOS = '/configuracion/calendario/periodos';

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  PENDIENTE: { label: 'Pendiente', bg: '#fef9c3', color: '#854d0e' },
  NUEVO: { label: 'Nuevo', bg: '#ecfdf5', color: '#047857' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
};

function TipoLabel({ tipo }: { tipo?: string | null }) {
  if (!tipo) return <span style={{ color: '#94a3b8' }}>—</span>;
  return <span>{formatTipoPeriodoLabel(tipo)}</span>;
}

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      background: cfg.bg,
      color: cfg.color,
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function instanciaLabel(row: Record<string, unknown>) {
  const periodoActividad = row.periodoActividad as { nombre?: string; id?: number } | undefined;
  const periodo = row.periodo as { descripcion?: string; id?: number } | undefined;
  const act = periodoActividad?.nombre ?? `#${periodoActividad?.id ?? '?'}`;
  const per = periodo?.descripcion ?? `#${periodo?.id ?? '?'}`;
  return `${act} · ${per}`;
}

function axiosErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail ?? data?.message ?? fallback;
  }
  return fallback;
}

function refreshList(
  dispatch: ReturnType<typeof useAppDispatch>,
  lastParams: Record<string, unknown> | null,
) {
  if (lastParams) dispatch(getEntities(lastParams));
}

export default function PeriodoInstanciaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.profasePeriodoActividadInstancia.entities);
  const totalItems = useAppSelector(s => s.profasePeriodoActividadInstancia.totalItems);
  const loading = useAppSelector(s => s.profasePeriodoActividadInstancia.loading);
  const updating = useAppSelector(s => s.profasePeriodoActividadInstancia.updating);
  const updateSuccess = useAppSelector(s => s.profasePeriodoActividadInstancia.updateSuccess);

  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [lastParams, setLastParams] = useState<Record<string, unknown> | null>(null);
  const [tipoResumen, setTipoResumen] = useState<PeriodoTipoResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);
  const [actionInstanciaId, setActionInstanciaId] = useState<number | null>(null);
  const pendingDeleteId = useRef<number | null>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const fetchTipoResumen = useCallback(async () => {
    setResumenLoading(true);
    try {
      const params = buildInstanciaTipoResumenParams(location.search);
      params.append('cacheBuster', String(Date.now()));
      const res = await axios.get<PeriodoTipoResumen[]>(
        `api/profase-periodo-actividad-instancias/tipo/resumen?${params.toString()}`,
      );
      setTipoResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTipoResumen([]);
    } finally {
      setResumenLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    fetchTipoResumen();
  }, [fetchTipoResumen]);

  useEffect(() => {
    const msg = (location.state as { successMsg?: string } | null)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!updateSuccess) return;
    dispatch(reset());
    dismiss();
    refreshList(dispatch, lastParams);
    fetchTipoResumen();
    if (pendingDeleteId.current != null) {
      showToast('success', 'Instancia de periodo eliminada correctamente.');
      pendingDeleteId.current = null;
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: Record<string, unknown>) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const handleTipoPeriodoFilter = useCallback((tipoPeriodo: string) => {
    setSearchParams(setTipoPeriodoFilter(searchParams, tipoPeriodo));
  }, [searchParams, setSearchParams]);

  const runArchivar = async (row: Record<string, unknown>) => {
    const id = row.id as number;
    setActionInstanciaId(id);
    try {
      await dispatch(archivarInstancia(id)).unwrap();
      dispatch(reset());
      showToast('success', `Instancia "${instanciaLabel(row)}" archivada.`);
      refreshList(dispatch, lastParams);
      fetchTipoResumen();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo archivar la instancia.'));
    } finally {
      setActionInstanciaId(null);
      dismiss();
    }
  };

  const handleArchivarClick = (row: Record<string, unknown>) => {
    ask(
      <>
        ¿Archivar la instancia <strong>{instanciaLabel(row)}</strong>?
        {' '}Los workflows asociados en proceso o finalizados también se archivarán.
      </>,
      () => { void runArchivar(row); },
      { title: 'Archivar instancia', confirmLabel: 'Archivar', variant: 'warning' },
    );
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRevertirClick = async (row: Record<string, unknown>) => {
    const id = row.id as number;
    setActionInstanciaId(id);
    try {
      await dispatch(revertirArchivadoInstancia(id)).unwrap();
      dispatch(reset());
      showToast('success', `Archivado revertido para "${instanciaLabel(row)}".`);
      refreshList(dispatch, lastParams);
      fetchTipoResumen();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo revertir el archivado.'));
    } finally {
      setActionInstanciaId(null);
    }
  };

  const renderActions = (params: { row: Record<string, unknown> }) => {
    const row = params.row;
    const estado = String(row.estado ?? '').toUpperCase();
    const busy = updating && actionInstanciaId === row.id;

    return (
      <RenderActions>
        <EditButton onClick={() => navigate(`${BASE}/${row.id}/edit`)} disabled={busy} />
        {estado === 'GENERADO' && (
          <button
            type="button"
            className="btn btn-outline-warning btn-sm"
            disabled={busy}
            title="Archivar"
            onClick={() => handleArchivarClick(row)}
          >
            Archivar
          </button>
        )}
        {estado === 'ARCHIVADO' && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={busy}
            title="Revertir archivado"
            onClick={() => { void handleRevertirClick(row); }}
          >
            Revertir
          </button>
        )}
        <DeleteButton
          disabled={busy}
          onClick={() => {
            pendingDeleteId.current = row.id as number;
            ask(
              <>¿Confirmas que deseas eliminar la instancia <strong>{instanciaLabel(row)}</strong>? Esta acción no se puede deshacer.</>,
              () => dispatch(deleteEntity(row.id as number)),
              { title: 'Eliminar instancia de periodo', confirmLabel: 'Eliminar', variant: 'danger' },
            );
            topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      </RenderActions>
    );
  };

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 0.85,
      renderCell: (p: { row: { estado?: string } }) => <EstadoBadge estado={p.row.estado} />,
    },
    {
      field: 'periodoActividad.nombre',
      headerName: 'Definición de periodo',
      flex: 1.6,
      renderCell: (p: { row: { periodoActividad?: { nombre?: string } } }) => p.row.periodoActividad?.nombre ?? '—',
    },
    {
      field: 'periodoActividad.tipoPeriodo',
      headerName: 'Tipo catálogo',
      flex: 0.85,
      renderCell: (p: { row: { periodoActividad?: { tipoPeriodo?: string } } }) => (
        <TipoLabel tipo={p.row.periodoActividad?.tipoPeriodo} />
      ),
    },
    {
      field: 'periodo.descripcion',
      headerName: 'Periodo',
      flex: 1.2,
      renderCell: (p: { row: { periodo?: { descripcion?: string } } }) => p.row.periodo?.descripcion?.trim() || '—',
    },
    {
      field: 'periodo.tipo',
      headerName: 'Tipo periodo',
      flex: 0.75,
      renderCell: (p: { row: { periodo?: { tipo?: string; tipoLabel?: string } } }) => (
        <TipoLabel tipo={p.row.periodo?.tipoLabel ?? p.row.periodo?.tipo} />
      ),
    },
    {
      field: 'periodo.estado',
      headerName: 'Estado periodo',
      flex: 0.85,
      renderCell: (p: { row: { periodo?: { estado?: string } } }) => <EstadoBadge estado={p.row.periodo?.estado} />,
    },
    { field: '__actions', headerName: '', flex: 1.4, renderCell: renderActions },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Periodos', to: PERIODOS, icon: <FaRegCalendarCheck /> },
        { label: 'Instancia de Periodo', icon: <FaClone /> },
      ]} />

      <PageHeader
        icon={<FaClone />}
        iconColor="#8b5cf6"
        title="Instancia de Periodo"
        subtitle="Instancias que vinculan definiciones de periodo con periodos fiscales concretos"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva instancia</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#8b5cf6',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <PeriodoTipoResumenBar
        rows={tipoResumen}
        loading={resumenLoading}
        onTipoClick={handleTipoPeriodoFilter}
        subtitle="Por tipo de catálogo"
        filterHint="tipo catálogo"
      />

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={PERIODO_INSTANCIA_TABLE_FILTERS}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="id"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
